import React, { useState, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Stack,
  Button,
  Snackbar,
  Alert,
  Box,
  Paper,
  Typography,
  Link,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { copy as userCopy } from "../utils/userFacingMessages";

/** Step 1 of the password reset: ask for the account email, the backend
 *  sends a link to /passwordreset/<uid>/<token> (see PasswordReset). */
function PasswordForgotten() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastSeverity, setToastSeverity] = useState("success");
  const [toastMessage, setToastMessage] = useState("");

  const showToast = useCallback((message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  }, []);

  const handleCloseToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = (email || "").trim().toLowerCase();
    if (!trimmed) {
      showToast(userCopy.resetEmailRequired, "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post("api/account/password_reset/", { email: trimmed });
      if (res.status >= 200 && res.status < 300) {
        showToast(userCopy.resetEmailSent, "success");
        setTimeout(() => navigate("/login"), 2200);
      } else {
        showToast(userCopy.resetEmailFailed, "error");
      }
    } catch (err) {
      console.error("Password reset request failed", err);
      const status = err?.response?.status;
      showToast(
        status === 429 ? userCopy.resetEmailThrottled : userCopy.resetEmailFailed,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      spacing={4}
      sx={{ mt: "2%", mb: 4 }}
    >
      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        <Stack
          sx={{
            width: "100%",
            py: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f6f8 0%, #ecedf1 100%)",
            border: "1px solid #dde1e7",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ px: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Reset password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Enter your email to receive a reset link.
            </Typography>
          </Box>
        </Stack>

        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            width: "100%",
            mt: 4,
            mb: 2,
            borderRadius: 3,
            border: "1px solid #e7ebf3",
            backgroundColor: "#ffffff",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={2.5} sx={{ maxWidth: "540px" }}>
            <TextField
              required
              id="email"
              label="Email"
              placeholder="you@example.com"
              onChange={(event) => setEmail(event.target.value)}
              value={email}
              type="email"
              autoComplete="email"
              fullWidth
              variant="outlined"
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
              <Link
                component={RouterLink}
                to="/login"
                underline="hover"
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                Back to login
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              endIcon={<Send />}
              sx={{
                height: "52px",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                px: 3,
                mt: 0.5,
              }}
            >
              {submitting ? "Sending…" : "Send link"}
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toastSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

export default PasswordForgotten;
